import { Link } from 'react-router-dom';
import { useDrawer } from '@/app/providers';

// =========================================================================
// Record drawer — a peek at one entity without leaving the page.
// Fork-owned (zone C).
//
// The demo opens these from table rows and chart hotspots: a small card of
// fields plus "Open full page". Keeping the peek and the page separate is what
// lets a fleet table stay put while someone checks three machines in a row.
//
// P2 ships the payload contract and a readable placeholder. P3 fills in
// per-entity rendering (condition band pill on a machine, status chip on a
// work order, stock level on a part, timeline on a case).
// =========================================================================

export type RecordEntity = 'machine' | 'work-order' | 'part' | 'case';

export type RecordPayload = {
  entity: RecordEntity;
  /** Business id — L-02, WO-48210, P-99231, IR-2026-015. */
  id: string;
  title: string;
  fields: { label: string; value: string }[];
  /** Route to the full page. Omit when the entity has no page yet. */
  to?: string;
};

const ENTITY_LABEL: Record<RecordEntity, string> = {
  machine: 'Machine',
  'work-order': 'Work order',
  part: 'Part',
  case: 'Case',
};

export function RecordDrawerBody({ data }: { data: Partial<RecordPayload> }) {
  const { closeAll } = useDrawer();

  if (!data || !data.entity || !data.id) {
    return (
      <div className="flex-1 p-5">
        <p style={{ color: 'var(--ink-secondary)', fontSize: 13 }}>
          No record attached to this drawer.
        </p>
      </div>
    );
  }

  const record = data as RecordPayload;

  return (
    <div className="scroll-soft flex flex-1 flex-col overflow-y-auto p-5">
      <div
        style={{
          color: 'var(--ink-tertiary)',
          fontSize: 10,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        {ENTITY_LABEL[record.entity]} · <span className="mono">{record.id}</span>
      </div>

      <h3 className="mt-1" style={{ color: 'var(--ink-primary)', fontSize: 15 }}>
        {record.title}
      </h3>

      <dl className="mt-4 flex flex-col gap-2">
        {(record.fields ?? []).map((field) => (
          <div
            key={field.label}
            className="flex items-baseline justify-between gap-4 py-1"
            style={{ borderBottom: '1px solid var(--hairline)' }}
          >
            <dt style={{ color: 'var(--ink-tertiary)', fontSize: 12 }}>
              {field.label}
            </dt>
            <dd
              className="mono text-right"
              style={{ color: 'var(--ink-primary)', fontSize: 12 }}
            >
              {field.value}
            </dd>
          </div>
        ))}
      </dl>

      {record.to ? (
        <Link
          to={record.to}
          onClick={closeAll}
          className="mt-5 inline-flex w-fit items-center rounded-lg px-3 py-1.5 text-sm font-medium"
          style={{ background: 'var(--accent)', color: 'var(--accent-text)' }}
        >
          Open full page
        </Link>
      ) : null}
    </div>
  );
}

export default RecordDrawerBody;
